import { Property } from "@/features/survey/types";
import { usePropertyImage } from "@/shared/hooks/usePropertyImage";
import { useEffect, useRef } from "react";

interface PropertyImagesCacheProps {
  properties: Property[];
  onImagesResolved: (imageCache: Record<string, string>) => void;
}

/**
 * Component that pre-loads images for multiple properties and provides a cache
 * This allows us to use resolved image URLs in non-React contexts like Mapbox popups
 */
const PropertyImagesCache = ({
  properties,
  onImagesResolved,
}: PropertyImagesCacheProps) => {
  const cacheRef = useRef<Record<string, string>>({});
  const resolvedCountRef = useRef(0);
  const totalPropertiesRef = useRef(0);

  // Track the total number of properties we need to resolve
  useEffect(() => {
    totalPropertiesRef.current = properties.length;
    resolvedCountRef.current = 0;
    cacheRef.current = {};
  }, [properties]);

  return (
    <>
      {properties.map((property) => (
        <PropertyImageResolver
          key={property.id}
          property={property}
          onImageResolved={(imageSrc) => {
            cacheRef.current[property.id] = imageSrc;
            resolvedCountRef.current += 1;

            // Call callback with current cache state (even if not all resolved yet)
            // This allows for progressive updates as images load
            onImagesResolved({ ...cacheRef.current });
          }}
        />
      ))}
    </>
  );
};

interface PropertyImageResolverProps {
  property: Property;
  onImageResolved: (imageSrc: string) => void;
}

const PropertyImageResolver = ({
  property,
  onImageResolved,
}: PropertyImageResolverProps) => {
  const { imageSrc, isLoading } = usePropertyImage(property);
  const hasResolvedRef = useRef(false);

  useEffect(() => {
    // Wait for loading to finish and ensure we have an image source
    if (!hasResolvedRef.current && !isLoading && imageSrc) {
      onImageResolved(imageSrc);
      hasResolvedRef.current = true;
    }
  }, [imageSrc, isLoading, onImageResolved, property.id]);

  useEffect(() => {
    // Reset resolved flag when property changes
    hasResolvedRef.current = false;
  }, [property.id]);

  return null;
};

export default PropertyImagesCache;
